'use client';

import { type ReactNode, useEffect, useState } from 'react';
import { LanguageProvider } from '@/i18n/LanguageProvider';
import {
  type AvailableLocale,
  LOCALE_MAPPING,
} from '@/i18n/LanguageProvider/shared';

interface ErrorLanguageProviderProps {
  children: ReactNode;
}

function getBrowserLocale(): AvailableLocale | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return LOCALE_MAPPING[navigator.language] || 'en-US';
}

export function ErrorLanguageProvider({
  children,
}: ErrorLanguageProviderProps) {
  const [locale, setLocale] = useState<AvailableLocale>('en-US');

  useEffect(() => {
    async function getLocale() {
      try {
        const browserLocale = getBrowserLocale();
        if (browserLocale) {
          setLocale(browserLocale);
          return;
        }

        setLocale('en-US');
      } catch (error) {
        console.error('Failed to get locale:', error);
        setLocale('en-US');
      }
    }

    getLocale();
  }, []);

  // An error boundary starts with no compiled set: react-intl then renders each
  // message's source string, which is the English copy, and the error path
  // loads no translation chunk unless the browser asks for another locale.
  return (
    <LanguageProvider
      locale={locale}
      initialMessages={{ locale: 'en-US', messages: {} }}
    >
      {children}
    </LanguageProvider>
  );
}
